/**
 * Copyright (c) 2025-2026 eBUCIS, LLC. All rights reserved.
 *
 * Referrals Dashboard - User's referral management page
 * Shows referral link, stats, milestones, and history
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  Gift,
  Copy,
  Check,
  Share2,
  Users,
  UserPlus,
  Clock,
  Trophy,
  Mail,
  ExternalLink,
  Loader2,
  AlertCircle,
  CheckCircle,
  ArrowRight,
  Send,
  RefreshCw,
  XCircle
} from 'lucide-react'
import {
  getMyReferralCode,
  getReferralStats,
  getReferralHistory,
  sendEmailInvite,
  resendInvite
} from '../services/referralService'
import toast from 'react-hot-toast'
import { createLogger } from '../utils/logger'

const logger = createLogger('Referrals')

const Referrals = () => {
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [profile, setProfile] = useState(null)
  const [stats, setStats] = useState(null)
  const [history, setHistory] = useState([])
  const [copied, setCopied] = useState(false)
  const [historyPage, setHistoryPage] = useState(1)
  const [hasMoreHistory, setHasMoreHistory] = useState(false)

  // Email invite state
  const [inviteEmail, setInviteEmail] = useState('')
  const [isSendingInvite, setIsSendingInvite] = useState(false)
  const [resendingId, setResendingId] = useState(null)

  // Build referral link
  const referralLink = profile?.referralCode
    ? `${window.location.origin}/join/${profile.referralCode}`
    : ''

  useEffect(() => {
    loadData()
    // Mark referrals page as visited (removes "New" indicator in header)
    localStorage.setItem('referrals_visited', 'true')
  }, [])

  const loadData = async () => {
    setIsLoading(true)
    setError(null)

    try {
      // Load profile, stats, and history in parallel
      const [profileRes, statsRes, historyRes] = await Promise.all([
        getMyReferralCode(),
        getReferralStats(),
        getReferralHistory({ page: 1, limit: 10 })
      ])

      if (profileRes.success) {
        setProfile(profileRes.data)
        // Update sessionStorage cache so Header badge stays in sync
        const inviteCount = profileRes.data.availableInvites ?? 0
        sessionStorage.setItem('referral_invite_count', inviteCount.toString())
        sessionStorage.setItem('referral_invite_count_time', Date.now().toString())
      }

      if (statsRes.success) {
        setStats(statsRes.data)
      }

      if (historyRes.success) {
        setHistory(historyRes.data.referrals || [])
        // Calculate hasMore from page and totalPages
        const currentPage = historyRes.data.page || 1
        const totalPages = historyRes.data.totalPages || 1
        setHasMoreHistory(currentPage < totalPages)
      }
    } catch (err) {
      logger.error('Error loading referral data:', err)
      setError('Failed to load referral information. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const loadMoreHistory = async () => {
    try {
      const nextPage = historyPage + 1
      const res = await getReferralHistory({ page: nextPage, limit: 10 })
      if (res.success) {
        setHistory((prev) => [...prev, ...(res.data.referrals || [])])
        // Calculate hasMore from page and totalPages
        const currentPage = res.data.page || nextPage
        const totalPages = res.data.totalPages || 1
        setHasMoreHistory(currentPage < totalPages)
        setHistoryPage(nextPage)
      }
    } catch (err) {
      logger.error('Error loading more history:', err)
      toast.error('Failed to load more referrals')
    }
  }

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(referralLink)
      setCopied(true)
      toast.success('Link copied to clipboard!')
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      toast.error('Failed to copy link')
    }
  }

  const shareViaEmail = () => {
    const subject = encodeURIComponent('Join me on Deaveris!')
    const body = encodeURIComponent(
      `Hey!\n\nI've been using Deaveris for SAT prep and thought you might find it useful. Here's my personal invite link:\n\n${referralLink}\n\nYou'll skip the waitlist and get direct access!`
    )
    window.open(`mailto:?subject=${subject}&body=${body}`)
  }

  const shareViaWhatsApp = () => {
    const text = encodeURIComponent(
      `Hey! Join me on Deaveris for SAT prep. Here's my invite link: ${referralLink}`
    )
    window.open(`https://wa.me/?text=${text}`, '_blank')
  }

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join Deaveris',
          text: 'Join me on Deaveris for SAT prep!',
          url: referralLink
        })
      } catch (err) {
        if (err.name !== 'AbortError') {
          toast.error('Failed to share')
        }
      }
    } else {
      copyToClipboard()
    }
  }

  // Send email invitation
  const handleSendEmailInvite = async (e) => {
    e.preventDefault()
    if (!inviteEmail || isSendingInvite) return

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(inviteEmail)) {
      toast.error('Please enter a valid email address')
      return
    }

    // Check if user has invites
    if (!profile?.availableInvites || profile.availableInvites <= 0) {
      toast.error('You have no invites remaining')
      return
    }

    setIsSendingInvite(true)
    try {
      const res = await sendEmailInvite(inviteEmail)
      if (res.success) {
        toast.success('Invitation sent successfully!')
        setInviteEmail('')
        // Reload data to update stats and history
        loadData()
      } else {
        toast.error(res.error?.message || 'Failed to send invitation')
      }
    } catch (err) {
      logger.error('Error sending invitation:', err)
      toast.error(err.response?.data?.error?.message || 'Failed to send invitation')
    } finally {
      setIsSendingInvite(false)
    }
  }

  // Resend email invitation
  const handleResendInvite = async (referralId) => {
    if (resendingId) return

    setResendingId(referralId)
    try {
      const res = await resendInvite(referralId)
      if (res.success) {
        toast.success('Invitation resent!')
        // Update the referral in history with new expiration
        setHistory((prev) =>
          prev.map((ref) =>
            ref.id === referralId ? { ...ref, inviteExpiresAt: res.data.expiresAt } : ref
          )
        )
      } else {
        toast.error(res.error?.message || 'Failed to resend invitation')
      }
    } catch (err) {
      logger.error('Error resending invitation:', err)
      toast.error(err.response?.data?.error?.message || 'Failed to resend invitation')
    } finally {
      setResendingId(null)
    }
  }

  // Get initials from publicName for avatar
  const getInitials = (publicName) => {
    if (!publicName) return '?'
    // PublicNames are like "StormHorse3471" - get first 2 chars
    return publicName.substring(0, 2).toUpperCase()
  }

  // Generate color based on publicName for consistent avatar colors
  const getAvatarColor = (publicName) => {
    if (!publicName) return 'bg-gray-400'
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-teal-500',
      'bg-orange-500'
    ]
    const hash = publicName.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
    return colors[hash % colors.length]
  }

  // Calculate milestone progress
  const getMilestoneProgress = () => {
    const totalSignups = stats?.successfulSignups || 0
    const milestones = [
      { signups: 5, bonus: 1 },
      { signups: 10, bonus: 2 },
      { signups: 25, bonus: 5 }
    ]

    // Find current milestone target
    const nextMilestone =
      milestones.find((m) => totalSignups < m.signups) || milestones[milestones.length - 1]
    const prevMilestone = milestones.filter((m) => totalSignups >= m.signups).pop()

    const progress = prevMilestone
      ? ((totalSignups - prevMilestone.signups) / (nextMilestone.signups - prevMilestone.signups)) *
        100
      : (totalSignups / nextMilestone.signups) * 100

    return {
      current: totalSignups,
      target: nextMilestone.signups,
      bonus: nextMilestone.bonus,
      progress: Math.min(progress, 100),
      completed: totalSignups >= nextMilestone.signups
    }
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 mx-auto text-primary-500 animate-spin mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading referral data...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Something went wrong
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <button
            onClick={loadData}
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  const milestone = getMilestoneProgress()

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-8"
      >
        <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full mb-4">
          <Gift className="w-8 h-8 text-primary-600 dark:text-primary-400" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Invite Friends to Deaveris
        </h1>
        <p className="text-gray-600 dark:text-gray-400 max-w-lg mx-auto">
          Share your personal invite link with friends. They'll skip the waitlist and you'll earn
          bonus invites as they sign up!
        </p>
      </motion.div>

      {/* Invites Remaining Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl p-6 mb-6 text-white shadow-lg"
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-primary-100 text-sm font-medium">Invites Remaining</p>
            <p className="text-4xl font-bold">{profile?.availableInvites || 0}</p>
          </div>
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
            <UserPlus className="w-8 h-8" />
          </div>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-primary-200" />
            <span>{profile?.totalInvitesUsed || 0} used</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-primary-200" />
            <span>{stats?.pendingReferrals || 0} pending</span>
          </div>
        </div>
      </motion.div>

      {/* Referral Link Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white dark:bg-gray-800 rounded-2xl p-6 mb-6 shadow-sm border border-gray-200 dark:border-gray-700"
      >
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Your Personal Invite Link
        </h2>

        {/* Link Display */}
        <div className="flex items-stretch gap-2 mb-4">
          <div className="flex-1 bg-gray-50 dark:bg-gray-900 rounded-lg px-4 py-3 font-mono text-sm text-gray-700 dark:text-gray-300 overflow-x-auto whitespace-nowrap">
            {referralLink}
          </div>
          <button
            onClick={copyToClipboard}
            className={`px-4 rounded-lg font-medium transition-all ${
              copied ? 'bg-green-500 text-white' : 'bg-primary-600 hover:bg-primary-700 text-white'
            }`}
          >
            {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
          </button>
        </div>

        {/* Share Buttons */}
        <div className="flex flex-wrap gap-3">
          <button
            onClick={shareViaWhatsApp}
            className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
            </svg>
            WhatsApp
          </button>

          <button
            onClick={shareViaEmail}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors"
          >
            <Mail className="w-5 h-5" />
            Email
          </button>

          <button
            onClick={shareNative}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium transition-colors"
          >
            <Share2 className="w-5 h-5" />
            Share...
          </button>
        </div>
      </motion.div>

      {/* Email Invite Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
        className="bg-white dark:bg-gray-800 rounded-2xl p-6 mb-6 shadow-sm border border-gray-200 dark:border-gray-700"
      >
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Invite by Email
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Send a personalized invitation directly to a friend's inbox.
        </p>

        <form onSubmit={handleSendEmailInvite} className="flex items-stretch gap-2">
          <div className="flex-1 relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="friend@example.com"
              disabled={isSendingInvite || !profile?.availableInvites}
              className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
          <button
            type="submit"
            disabled={isSendingInvite || !inviteEmail || !profile?.availableInvites}
            className="px-5 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center gap-2"
          >
            {isSendingInvite ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
            <span className="hidden sm:inline">Send Invite</span>
          </button>
        </form>

        {!profile?.availableInvites && (
          <p className="mt-3 text-sm text-amber-600 dark:text-amber-400">
            You've used all your invites. Earn more by getting friends to sign up!
          </p>
        )}
      </motion.div>

      {/* Stats Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6"
      >
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Signed Up</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats?.successfulSignups || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Pending</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {stats?.pendingReferrals || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
              <Trophy className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Total Used</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {profile?.totalInvitesUsed || 0}
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Milestone Progress */}
      {!milestone.completed &&
        (() => {
          const signupsNeeded = milestone.target - milestone.current
          const availableInvites = profile?.availableInvites || 0
          const pendingReferrals = profile?.pendingReferrals || stats?.pendingReferrals || 0
          // Max possible signups = current + pending that might convert + new invites we can send
          const maxPossibleSignups = milestone.current + pendingReferrals + availableInvites
          const canReachMilestone = maxPossibleSignups >= milestone.target

          return (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 mb-6 shadow-sm border border-gray-200 dark:border-gray-700"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Next Milestone
                </h2>
                <span className="text-sm font-medium text-primary-600 dark:text-primary-400">
                  +{milestone.bonus} invite{milestone.bonus > 1 ? 's' : ''} at {milestone.target}{' '}
                  signups
                </span>
              </div>

              <div className="mb-2">
                <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-1">
                  <span>{milestone.current} signups</span>
                  <span>{milestone.target} target</span>
                </div>
                <div className="h-3 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${milestone.progress}%` }}
                    transition={{ duration: 0.8, ease: 'easeOut' }}
                    className="h-full bg-gradient-to-r from-primary-500 to-primary-600 rounded-full"
                  />
                </div>
              </div>

              {canReachMilestone ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Get {signupsNeeded} more signup{signupsNeeded > 1 ? 's' : ''} to earn bonus
                  invites!
                  {pendingReferrals > 0 && availableInvites === 0 && (
                    <span className="text-primary-600 dark:text-primary-400">
                      {' '}
                      ({pendingReferrals} pending)
                    </span>
                  )}
                </p>
              ) : (
                <div className="flex items-start gap-2 text-sm">
                  <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  <p className="text-amber-600 dark:text-amber-400">
                    You need {signupsNeeded} more but can only get{' '}
                    {pendingReferrals + availableInvites}
                    {pendingReferrals > 0 && availableInvites > 0
                      ? ` (${pendingReferrals} pending + ${availableInvites} to send)`
                      : pendingReferrals > 0
                        ? ` (${pendingReferrals} pending)`
                        : ` (${availableInvites} invite${availableInvites !== 1 ? 's' : ''} left)`}
                    . New invites are granted monthly!
                  </p>
                </div>
              )}
            </motion.div>
          )
        })()}

      {/* Referral History */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
      >
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Your Referrals</h2>
        </div>

        {history.length === 0 ? (
          <div className="p-8 text-center">
            <Users className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
            <p className="text-gray-500 dark:text-gray-400">
              No referrals yet. Share your link to get started!
            </p>
          </div>
        ) : (
          <>
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {history.map((referral) => {
                const publicName = referral.referredUser?.publicName
                const hasSignedUp =
                  referral.status === 'signed_up' || referral.status === 'subscribed'
                const isEmailInvite = !!referral.inviteToken
                const isPending = referral.status === 'pending'
                const isExpired = referral.status === 'expired'

                return (
                  <div
                    key={referral.id}
                    className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      {/* Avatar with initials for signed up users, status icon for pending/expired */}
                      {hasSignedUp && publicName ? (
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${getAvatarColor(publicName)}`}
                        >
                          {getInitials(publicName)}
                        </div>
                      ) : (
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            referral.status === 'signed_up'
                              ? 'bg-green-100 dark:bg-green-900/30'
                              : referral.status === 'subscribed'
                                ? 'bg-purple-100 dark:bg-purple-900/30'
                                : referral.status === 'expired'
                                  ? 'bg-gray-100 dark:bg-gray-700'
                                  : 'bg-yellow-100 dark:bg-yellow-900/30'
                          }`}
                        >
                          {referral.status === 'signed_up' ? (
                            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                          ) : referral.status === 'subscribed' ? (
                            <Trophy className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                          ) : referral.status === 'expired' ? (
                            <XCircle className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                          ) : isEmailInvite ? (
                            <Mail className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                          ) : (
                            <Clock className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                          )}
                        </div>
                      )}
                      <div>
                        {/* Show publicName as primary text for signed up users */}
                        {hasSignedUp && publicName ? (
                          <>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-gray-900 dark:text-white">
                                {publicName}
                              </p>
                            </div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {referral.referredEmail} · Signed up{' '}
                              {new Date(referral.signedUpAt).toLocaleDateString()}
                            </p>
                          </>
                        ) : isExpired ? (
                          <>
                            <p className="font-medium text-gray-500 dark:text-gray-400">
                              {referral.referredEmail}
                            </p>
                            <p className="text-sm text-gray-400 dark:text-gray-500">
                              Expired · Invite refunded
                            </p>
                          </>
                        ) : isEmailInvite && isPending ? (
                          <>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {referral.referredEmail}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              Email sent{' '}
                              {referral.invitedAt
                                ? new Date(referral.invitedAt).toLocaleDateString()
                                : ''}{' '}
                              · Expires{' '}
                              {referral.inviteExpiresAt
                                ? new Date(referral.inviteExpiresAt).toLocaleDateString()
                                : ''}
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {referral.referredEmail}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              Invited {new Date(referral.createdAt).toLocaleDateString()}
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Resend button for pending email invites */}
                      {isEmailInvite && isPending && (
                        <button
                          onClick={() => handleResendInvite(referral.id)}
                          disabled={resendingId === referral.id}
                          className="px-3 py-1 text-sm text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg flex items-center gap-1 transition-colors disabled:opacity-50"
                          title="Resend invitation email"
                        >
                          {resendingId === referral.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <RefreshCw className="w-4 h-4" />
                          )}
                          <span className="hidden sm:inline">Resend</span>
                        </button>
                      )}
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          referral.status === 'signed_up'
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                            : referral.status === 'subscribed'
                              ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                              : referral.status === 'expired'
                                ? 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                                : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                        }`}
                      >
                        {referral.status === 'signed_up'
                          ? 'Signed Up'
                          : referral.status === 'subscribed'
                            ? 'Subscribed'
                            : referral.status === 'expired'
                              ? 'Expired'
                              : 'Pending'}
                      </span>
                    </div>
                  </div>
                )
              })}
            </div>

            {hasMoreHistory && (
              <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={loadMoreHistory}
                  className="w-full py-2 text-center text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium flex items-center justify-center gap-2"
                >
                  Load more
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        )}
      </motion.div>

      {/* How it works section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="mt-8 text-center"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">How It Works</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-sm text-gray-600 dark:text-gray-400">
          <div>
            <div className="w-10 h-10 mx-auto mb-2 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
              <span className="text-primary-600 dark:text-primary-400 font-bold">1</span>
            </div>
            <p>Share your personal invite link with friends</p>
          </div>
          <div>
            <div className="w-10 h-10 mx-auto mb-2 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
              <span className="text-primary-600 dark:text-primary-400 font-bold">2</span>
            </div>
            <p>They click the link and skip the waitlist</p>
          </div>
          <div>
            <div className="w-10 h-10 mx-auto mb-2 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
              <span className="text-primary-600 dark:text-primary-400 font-bold">3</span>
            </div>
            <p>You earn bonus invites as they sign up</p>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export default Referrals
